import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OwnCallerIdCard } from "@/components/OwnCallerIdCard";
import { toast } from "sonner";
import { Phone, Loader2, User, ShieldCheck, MessageSquareText, KeyRound } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { InviteEarnCard } from "@/components/InviteEarnCard";

const ChangePasswordCard = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — use it on your next sign-in");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full bg-zinc-50 border border-black/10 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary outline-none transition-all";
  return (
    <form onSubmit={submit} className="bg-white border border-black/10 rounded-2xl p-6 shadow-sm mb-6" data-testid="change-password-form">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="h-4 w-4 text-primary" />
        <h2 className="font-heading font-bold">Change password</h2>
      </div>
      <p className="text-zinc-500 text-sm mb-4">
        You'll need your current password. Forgot it? Sign out and use <b>"Forgot password?"</b> on the login page instead.
      </p>
      <div className="space-y-3">
        <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required
          placeholder="Current password" autoComplete="current-password"
          data-testid="current-password-input" className={inputCls} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} required
            placeholder="New password (min 6 chars)" autoComplete="new-password"
            data-testid="new-password-input" className={inputCls} />
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
            placeholder="Repeat new password" autoComplete="new-password"
            data-testid="confirm-password-input" className={inputCls} />
        </div>
      </div>
      <button type="submit" disabled={busy} data-testid="change-password-submit"
        className="mt-4 px-5 py-2.5 rounded-xl bg-primary text-white font-semibold hover:bg-[#1E40AF] transition-colors flex items-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Update password
      </button>
    </form>
  );
};

export default function Settings() {
  const { user, setUserData } = useAuth();
  const [phone, setPhone] = useState(user?.phone || "");
  const [callbackNumber, setCallbackNumber] = useState(user?.callback_number || "");
  const [smsFollowup, setSmsFollowup] = useState(user?.sms_followup ?? true);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profile", {
        phone: phone.trim(),
        callback_number: callbackNumber.trim(),
        sms_followup: smsFollowup,
      });
      setUserData(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleSms = async (checked) => {
    setSmsFollowup(checked);
    try {
      const { data } = await api.put("/profile", {
        phone: phone.trim(),
        callback_number: callbackNumber.trim(),
        sms_followup: checked,
      });
      setUserData(data);
      toast.success(checked ? "SMS follow-up enabled" : "SMS follow-up disabled");
    } catch (err) {
      setSmsFollowup(!checked);
      toast.error(apiError(err.response?.data?.detail));
    }
  };


  return (
    <div className="max-w-xl mx-auto">
      <h1 className="font-heading text-2xl font-bold tracking-tight mb-1">Settings</h1>
      <p className="text-zinc-500 text-sm mb-6">Manage your account and callback number.</p>

      <div className="bg-white border border-black/10 rounded-2xl p-6 shadow-sm mb-6">
        <div className="flex items-center gap-2 mb-4">
          <User className="h-4 w-4 text-zinc-400" />
          <h2 className="font-heading font-bold">Account</h2>
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-400 font-bold">Name</div>
            <div className="font-semibold">{user?.name}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-zinc-400 font-bold">Email</div>
            <div className="font-semibold">{user?.email}</div>
          </div>
        </div>
      </div>

      <InviteEarnCard />

      <ChangePasswordCard />

      <form onSubmit={save} className="bg-white border border-black/10 rounded-2xl p-6 shadow-sm" data-testid="settings-phone-form">
        <div className="flex items-center gap-2 mb-1">
          <Phone className="h-4 w-4 text-primary" />
          <h2 className="font-heading font-bold">Your callback number</h2>
        </div>
        <p className="text-zinc-500 text-sm mb-4">
          When you place a call, we ring <b>this</b> phone first, then connect you to the destination —
          so calls work even on weak internet. Use full international format, e.g. <span className="font-mono">+2348012345678</span>.
        </p>
        <div className="flex gap-3">
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+234 801 234 5678"
            data-testid="settings-phone-input"
            className="flex-1 bg-zinc-50 border border-black/10 rounded-xl px-4 py-3 font-mono focus:ring-2 focus:ring-primary outline-none transition-all"
          />
          <button
            type="submit"
            disabled={saving}
            data-testid="settings-phone-save"
            className="px-5 rounded-xl bg-primary text-white font-semibold hover:bg-[#1E40AF] transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </button>
        </div>
        <div className="mt-4 flex items-center gap-2 text-xs text-zinc-400">
          <ShieldCheck className="h-3.5 w-3.5" /> Your number is stored securely and only used to connect your calls.
        </div>

        <div className="mt-6 pt-6 border-t border-black/10">
          <div className="flex items-center gap-2 mb-1">
            <Phone className="h-4 w-4 text-primary" />
            <h2 className="font-heading font-bold">Preferred callback number <span className="text-zinc-400 font-normal text-sm">(optional)</span></h2>
          </div>
          <p className="text-zinc-500 text-sm mb-4">
            If someone you called misses it and dials your ZEEPTEL number back, we announce <b>this</b> number
            so they know how to reach you. Leave blank to use your callback phone above.
          </p>
          <input
            type="tel"
            value={callbackNumber}
            onChange={(e) => setCallbackNumber(e.target.value)}
            placeholder="+1 651 347 2708"
            data-testid="settings-callback-input"
            className="w-full bg-zinc-50 border border-black/10 rounded-xl px-4 py-3 font-mono focus:ring-2 focus:ring-primary outline-none transition-all"
          />
        </div>

        <div className="mt-6 pt-6 border-t border-black/10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <MessageSquareText className="h-4 w-4 text-primary" />
                <h2 className="font-heading font-bold">Text them when they miss your call</h2>
              </div>
              <p className="text-zinc-500 text-sm">
                If the person doesn&rsquo;t answer, we text them your name and call-back number so
                they can reach you. Charged to your wallet per text (about $0.05 to the US, $0.40–0.75 to
                most African countries); you&rsquo;ll hear the exact price on the call. Not sent when your own
                number is already shown to the receiver.
              </p>
            </div>
            <Switch
              checked={smsFollowup}
              onCheckedChange={toggleSms}
              data-testid="settings-sms-toggle"
              aria-label="Toggle SMS follow-up"
            />
          </div>
        </div>

        <OwnCallerIdCard
          initial={user?.own_caller_id}
          defaultNumber={user?.callback_number || user?.phone}
          onChange={(oc) => setUserData({ ...user, own_caller_id: oc })}
        />

      </form>
    </div>
  );
}
